import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  CheckSquare,
  Bot,
  Plug,
  Shield,
  Wallet,
  TrendingUp,
  FileText,
  Users,
  Building2,
  UsersRound,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/dashboard",   label: "Dashboard",   icon: LayoutDashboard, testid: "nav-dashboard", key: "dashboard" },
  { to: "/tasks",       label: "Task List",   icon: CheckSquare,     testid: "nav-tasks",     key: "tasks" },
  { to: "/agent",       label: "Agent",       icon: Bot,             testid: "nav-agent",     key: "agent" },
  { to: "/integrations", label: "APIs",        icon: Plug,            testid: "nav-apis",      key: "apis" },
  { to: "/permissions", label: "Permissions", icon: Shield,          testid: "nav-permissions", key: "permissions" },
  { to: "/cost",        label: "Cost",        icon: Wallet,          testid: "nav-cost",      key: "cost" },
  { to: "/adoption",    label: "Adoption",    icon: TrendingUp,      testid: "nav-adoption",  key: "adoption" },
  { to: "/content",     label: "Content",     icon: FileText,        testid: "nav-content",   key: "content" },
  { to: "/users",       label: "Users",       icon: Users,           testid: "nav-users",     key: "users" },
  { to: "/accounts",    label: "Accounts",    icon: Building2,       testid: "nav-accounts",  key: "accounts" },
  { to: "/user-section", label: "User Section", icon: UsersRound,    testid: "nav-user-section", key: "user-section" },
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const granted = new Set(user?.permissions || []);
  const visibleNav = isOwner ? NAV : NAV.filter((n) => granted.has(n.key));
  return (
    <aside
      data-testid="sidebar"
      className={`relative shrink-0 ${collapsed ? "w-16" : "w-64"} bg-white border-r border-slate-200 transition-all duration-300 ease-in-out flex flex-col`}
    >
      {/* Brand */}
      <div className="h-20 px-3 flex items-center gap-3 border-b border-slate-100 overflow-hidden">
        {collapsed ? (
          <div className="h-11 w-11 shrink-0 rounded-xl btn-brand flex items-center justify-center">
            <span className="font-display font-bold text-white text-lg">3</span>
          </div>
        ) : (
          <img
            src="https://customer-assets.emergentagent.com/job_three-zinc/artifacts/h16r009z_Three%20ZInc%20logo.png"
            alt="Three Zinc"
            className="h-14 w-auto select-none"
            data-testid="sidebar-brand-logo"
          />
        )}
      </div>

      {/* Nav */}
      <TooltipProvider delayDuration={80}>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {!collapsed && (
            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Workspace
            </p>
          )}
        {visibleNav.map(({ to, label, icon: Icon, testid }) => {
          const link = (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 h-10 px-3 rounded-lg text-sm font-medium transition-all ${
                  collapsed ? "justify-center" : ""
                } ${
                  isActive
                    ? collapsed
                      ? "bg-sky-50 text-blue-700"
                      : "btn-brand"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? (collapsed ? "text-blue-600" : "text-white") : "text-slate-500 group-hover:text-slate-900"}`} />
                  {!collapsed && <span className="truncate">{label}</span>}
                </>
              )}
            </NavLink>
          );

          if (!collapsed) return link;

          return (
            <Tooltip key={to} delayDuration={80}>
              <TooltipTrigger asChild>{link}</TooltipTrigger>
              <TooltipContent side="right" sideOffset={12} className="bg-slate-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-md">
                {label}
              </TooltipContent>
            </Tooltip>
          );
        })}
        </nav>
      </TooltipProvider>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        data-testid="sidebar-toggle"
        className="absolute -right-3 top-20 h-6 w-6 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 shadow-soft z-10"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-slate-600" /> : <ChevronLeft className="h-3.5 w-3.5 text-slate-600" />}
      </button>

      {/* Footer */}
      <div className="p-3 border-t border-slate-100">
        {!collapsed ? (
          <div className="rounded-xl bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50 p-3 border border-slate-100">
            <p className="text-xs font-semibold text-slate-800">Free plan</p>
            <p className="text-[11px] text-slate-500 mt-0.5">3 of 10 projects used</p>
            <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
              <div className="h-full w-[30%] btn-brand rounded-full" />
            </div>
          </div>
        ) : (
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full w-[30%] btn-brand rounded-full" />
          </div>
        )}
      </div>
    </aside>
  );
}
